function shaped(value: string, timeZone: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value

  const parts = new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone,
  }).formatToParts(date)

  const partOf = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? ''
  return `${partOf('month')} ${partOf('day')}, ${partOf('year')}`
}

/** `MM/DD/YYYY` at UTC: a date of birth is a date, not an instant. */
export function formatDateOfBirth(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value

  const parts = new Intl.DateTimeFormat('en-US', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  }).formatToParts(date)

  const partOf = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? ''
  return `${partOf('month')}/${partOf('day')}/${partOf('year')}`
}

/** The report's own `date`, a calendar date, so read at UTC. */
export function formatReportDate(value: string): string {
  return shaped(value, 'UTC')
}

function partsOf(date: Date, timeZone: string, options: Intl.DateTimeFormatOptions) {
  const parts = new Intl.DateTimeFormat(undefined, { ...options, timeZone }).formatToParts(date)
  return (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? ''
}

function clock(date: Date, timeZone: string): string {
  const partOf = partsOf(date, timeZone, { hour: 'numeric', minute: '2-digit', hour12: true })
  return `${partOf('hour')}:${partOf('minute')} ${partOf('dayPeriod').toUpperCase()}`
}

/** `October 14, 2026, 7:30 PM`: an instant's date and time, in the organization's zone. */
export function formatInstantDateTime(value: string, timeZone: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return `${shaped(value, timeZone)}, ${clock(date, timeZone)}`
}

/** `Wed, Oct 14, 7:30 PM`: an instant, compactly, in the organization's zone. */
export function formatMoment(value: string, timeZone: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value

  const partOf = partsOf(date, timeZone, { weekday: 'short', day: 'numeric', month: 'short' })
  return `${partOf('weekday')}, ${partOf('month')} ${partOf('day')}, ${clock(date, timeZone)}`
}

/** `Sep 18`: `days` calendar days after the instant's local day in `timeZone`. */
export function visitByDate(resolvedAt: string, days: number, timeZone: string): string | null {
  const resolved = new Date(resolvedAt)
  if (Number.isNaN(resolved.getTime())) return null

  const local = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone,
  }).format(resolved)

  const [year, month, day] = local.split('-').map(Number)
  const due = new Date(Date.UTC(year, month - 1, day + days))

  const parts = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' }).formatToParts(due)
  const partOf = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? ''
  return `${partOf('month')} ${partOf('day')}`
}
